import React, { useState, useMemo, useCallback } from 'react';
import {
  Box, Typography, Button, ToggleButton, ToggleButtonGroup,
  Tabs, Tab, Table, TableHead, TableRow, TableCell, TableBody,
  TableSortLabel, CircularProgress, Alert, Chip, Tooltip,
  Divider, Paper,
} from '@mui/material';
import { Download, InfoOutlined } from '@mui/icons-material';
import { format, subMonths, startOfMonth, endOfMonth } from 'date-fns';
import api from '../../services/api';

// ─── NHS colour palette ───────────────────────────────────────────────────────

const C = {
  blue:   '#005EB8',
  green:  '#009639',
  yellow: '#FFB81C',
  red:    '#DA291C',
  dark:   '#425563',
  mid:    '#768692',
  pale:   '#E8EDEE',
  white:  '#FFFFFF',
};

// ─── Date preset helpers ──────────────────────────────────────────────────────

function presetDates(preset) {
  const today = new Date();
  const map = {
    '3m':  [subMonths(today, 2),  today],
    '6m':  [subMonths(today, 5),  today],
    '12m': [subMonths(today, 11), today],
  };
  const [s, e] = map[preset] || [];
  if (!s) return null;
  return { start: format(startOfMonth(s), 'yyyy-MM-dd'), end: format(endOfMonth(e), 'yyyy-MM-dd') };
}

// ─── Colour helpers ───────────────────────────────────────────────────────────

function complianceFg(pct) {
  if (pct >= 90) return C.green;
  if (pct >= 70) return '#7B5800';
  return C.red;
}
function complianceBg(pct) {
  if (pct >= 90) return '#E8F5E9';
  if (pct >= 70) return '#FFF8E1';
  return '#FFEBEE';
}
function noticeFg(days) {
  if (days >= 28) return C.green;
  if (days >= 14) return '#7B5800';
  return C.red;
}
function noticeBg(days) {
  if (days >= 28) return '#E8F5E9';
  if (days >= 14) return '#FFF8E1';
  return '#FFEBEE';
}

// ─── SVG Stacked Bar Chart ────────────────────────────────────────────────────

const VB_W = 900, VB_H = 220;
const PAD  = { top: 30, right: 24, bottom: 52, left: 52 };
const CW   = VB_W - PAD.left - PAD.right;
const CH   = VB_H - PAD.top  - PAD.bottom;

function CancellationChart({ timeSeries }) {
  if (!timeSeries || timeSeries.length === 0) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 160, color: C.mid }}>
        <Typography variant="body2">No data for selected period</Typography>
      </Box>
    );
  }

  const n      = timeSeries.length;
  const maxY   = Math.max(...timeSeries.map(d => d.total), 1);
  const yMax   = Math.ceil(maxY / 50) * 50;  // round to nearest 50

  const barW   = Math.min(60, (CW / n) * 0.6);
  const gap    = (CW - n * barW) / (n + 1);
  const barX   = i => PAD.left + gap + i * (barW + gap);
  const yAt    = v  => PAD.top + CH - (v / yMax) * CH;

  const yTicks = [];
  const tickStep = yMax <= 100 ? 10 : yMax <= 500 ? 50 : yMax <= 2000 ? 200 : 500;
  for (let v = 0; v <= yMax; v += tickStep) yTicks.push(v);

  const xLabels = timeSeries.map(d => {
    const [yr, mo] = d.period.split('-');
    const dt = new Date(+yr, +mo - 1, 1);
    return { abbr: format(dt, 'MMM'), yr: String(yr).slice(2) };
  });

  return (
    <Box>
      <svg viewBox={`0 0 ${VB_W} ${VB_H}`} width="100%" preserveAspectRatio="xMidYMid meet"
        style={{ display: 'block', overflow: 'visible' }}>

        {/* Grid lines */}
        {yTicks.map(v => (
          <g key={v}>
            <line x1={PAD.left} y1={yAt(v)} x2={PAD.left + CW} y2={yAt(v)}
              stroke={v === 0 ? C.dark : C.pale} strokeWidth={v === 0 ? 1 : 0.5} />
            <text x={PAD.left - 6} y={yAt(v) + 4} textAnchor="end" fontSize={9} fill={C.mid}>{v}</text>
          </g>
        ))}

        {/* Bars */}
        {timeSeries.map((d, i) => {
          const x            = barX(i);
          const compliantH   = (d.compliant     / yMax) * CH;
          const nonCompH     = (d.non_compliant / yMax) * CH;
          const totalH       = compliantH + nonCompH;
          const pctLabel     = d.total > 0 ? `${d.pct_compliant}%` : '';

          return (
            <g key={i}>
              {/* Compliant (bottom, green) */}
              {compliantH > 0 && (
                <rect x={x} y={yAt(d.compliant)} width={barW} height={compliantH}
                  fill={C.green} opacity={0.85} rx={2}>
                  <title>{d.period}: {d.compliant} compliant (≥4 weeks)</title>
                </rect>
              )}
              {/* Non-compliant (top, red) */}
              {nonCompH > 0 && (
                <rect x={x} y={yAt(d.total)} width={barW} height={nonCompH}
                  fill={C.red} opacity={0.8} rx={2}>
                  <title>{d.period}: {d.non_compliant} non-compliant (&lt;4 weeks)</title>
                </rect>
              )}
              {/* Compliance % label above bar */}
              {d.total > 0 && (
                <text x={x + barW / 2} y={yAt(d.total) - 5}
                  textAnchor="middle" fontSize={9} fontWeight="600"
                  fill={complianceFg(d.pct_compliant)}>
                  {pctLabel}
                </text>
              )}
              {/* Total label inside bar if tall enough */}
              {totalH > 16 && (
                <text x={x + barW / 2} y={yAt(0) - totalH / 2 + 4}
                  textAnchor="middle" fontSize={8} fill={C.white} opacity={0.9}>
                  {d.total}
                </text>
              )}
            </g>
          );
        })}

        {/* X-axis labels */}
        {xLabels.map((lbl, i) => (
          <g key={i}>
            <text x={barX(i) + barW / 2} y={PAD.top + CH + 16}
              textAnchor="middle" fontSize={9.5} fill={C.mid}>{lbl.abbr}</text>
            <text x={barX(i) + barW / 2} y={PAD.top + CH + 27}
              textAnchor="middle" fontSize={8} fill={C.pale}>'{lbl.yr}</text>
          </g>
        ))}

        {/* Chart border */}
        <rect x={PAD.left} y={PAD.top} width={CW} height={CH}
          fill="none" stroke={C.pale} strokeWidth={1} />
      </svg>

      {/* Legend */}
      <Box sx={{ display: 'flex', gap: 2.5, mt: 0.5, flexWrap: 'wrap', pl: 0.5 }}>
        <LegBar color={C.green} label="Compliant — ≥4 weeks' notice" />
        <LegBar color={C.red}   label="Non-compliant — <4 weeks' notice" />
        <Typography sx={{ fontSize: '0.65rem', color: C.mid, alignSelf: 'center' }}>
          % above each bar = compliance rate for that month
        </Typography>
      </Box>
    </Box>
  );
}

function LegBar({ color, label }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      <Box sx={{ width: 14, height: 14, bgcolor: color, borderRadius: '2px', opacity: 0.85 }} />
      <Typography sx={{ fontSize: '0.65rem', color: C.dark }}>{label}</Typography>
    </Box>
  );
}

// ─── Summary Card ─────────────────────────────────────────────────────────────

function StatCard({ label, primary, secondary, accentColor }) {
  return (
    <Paper elevation={0} sx={{
      flex: 1, minWidth: 140, p: 2, borderRadius: 2,
      border: `1.5px solid ${accentColor}30`,
      background: `linear-gradient(135deg, ${accentColor}08 0%, ${C.white} 100%)`,
    }}>
      <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase',
        letterSpacing: 0.8, color: C.mid, mb: 0.5 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: '1.8rem', fontWeight: 800, lineHeight: 1, color: accentColor }}>
        {primary}
      </Typography>
      {secondary && (
        <Typography sx={{ fontSize: '0.7rem', color: C.mid, mt: 0.5 }}>{secondary}</Typography>
      )}
    </Paper>
  );
}

// ─── Breakdown Table ──────────────────────────────────────────────────────────

const COLS = [
  { id: 'name',             label: 'Name',               align: 'left'  },
  { id: 'total',            label: 'Total Cancelled',    align: 'right' },
  { id: 'compliant',        label: '≥4 Wks (Compliant)', align: 'right' },
  { id: 'pct_compliant',    label: '% Compliant',        align: 'right' },
  { id: 'non_compliant',    label: '<4 Wks (Non-compliant)', align: 'right' },
  { id: 'pct_non_compliant',label: '% Non-compliant',   align: 'right' },
  { id: 'avg_notice_days',  label: 'Avg Notice (days)',  align: 'right' },
  { id: 'reallocated_adhoc',label: 'Reallocated Ad Hoc', align: 'right' },
];

function BreakdownTable({ rows }) {
  const [sortKey, setSortKey] = useState('total');
  const [sortDir, setSortDir] = useState('desc');

  const handleSort = key => {
    if (key === sortKey) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  const sorted = useMemo(() => [...rows].sort((a, b) => {
    const va = a[sortKey] ?? 0, vb = b[sortKey] ?? 0;
    if (typeof va === 'string') return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
    return sortDir === 'asc' ? va - vb : vb - va;
  }), [rows, sortKey, sortDir]);

  if (!rows.length) return (
    <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
      No data for this breakdown
    </Typography>
  );

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ backgroundColor: C.blue }}>
            {COLS.map(col => (
              <TableCell key={col.id} align={col.align}
                sortDirection={sortKey === col.id ? sortDir : false}
                sx={{ py: 1, whiteSpace: 'nowrap', borderBottom: 'none' }}>
                <TableSortLabel
                  active={sortKey === col.id}
                  direction={sortKey === col.id ? sortDir : 'asc'}
                  onClick={() => handleSort(col.id)}
                  sx={{
                    color: `${C.white} !important`,
                    '& .MuiTableSortLabel-icon': { color: `${C.white} !important`, opacity: 0.65 },
                    fontSize: '0.68rem', fontWeight: 700, letterSpacing: 0.3, textTransform: 'uppercase',
                  }}>
                  {col.label}
                </TableSortLabel>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.map((row, i) => (
            <TableRow key={i} hover sx={{ backgroundColor: i % 2 === 0 ? C.white : '#F7FAFC' }}>
              <TableCell sx={{ fontWeight: 500, fontSize: '0.78rem' }}>{row.name}</TableCell>
              <TableCell align="right" sx={{ fontSize: '0.78rem', fontWeight: 600 }}>
                {row.total.toLocaleString()}
              </TableCell>
              <TableCell align="right" sx={{ fontSize: '0.78rem', color: C.green }}>
                {row.compliant.toLocaleString()}
              </TableCell>
              <TableCell align="right">
                <Box sx={{ display: 'inline-block', px: 1, py: 0.25, borderRadius: 1,
                  fontSize: '0.78rem', fontWeight: 700,
                  color: complianceFg(row.pct_compliant), bgcolor: complianceBg(row.pct_compliant) }}>
                  {row.pct_compliant}%
                </Box>
              </TableCell>
              <TableCell align="right" sx={{
                fontSize: '0.78rem',
                color: row.non_compliant > 0 ? C.red : C.mid,
                fontWeight: row.non_compliant > 0 ? 600 : 400,
              }}>
                {row.non_compliant.toLocaleString()}
              </TableCell>
              <TableCell align="right">
                <Box sx={{ display: 'inline-block', px: 1, py: 0.25, borderRadius: 1,
                  fontSize: '0.78rem', fontWeight: 700,
                  color: row.non_compliant > 0 ? C.red : C.mid,
                  bgcolor: row.non_compliant > 0 ? '#FFEBEE' : 'transparent' }}>
                  {row.pct_non_compliant}%
                </Box>
              </TableCell>
              <TableCell align="right">
                <Box sx={{ display: 'inline-block', px: 1, py: 0.25, borderRadius: 1,
                  fontSize: '0.78rem', fontWeight: 700,
                  color: noticeFg(row.avg_notice_days), bgcolor: noticeBg(row.avg_notice_days) }}>
                  {row.avg_notice_days}d
                </Box>
              </TableCell>
              <TableCell align="right" sx={{ fontSize: '0.78rem' }}>
                {row.reallocated_adhoc > 0
                  ? <Chip label={row.reallocated_adhoc} size="small"
                      sx={{ fontSize: '0.7rem', height: 20, bgcolor: '#E8F5E9', color: C.green, fontWeight: 700 }} />
                  : <Typography sx={{ fontSize: '0.78rem', color: C.pale }}>—</Typography>
                }
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

// ─── CSV download ─────────────────────────────────────────────────────────────

function exportCSV(rows, dates) {
  const header = COLS.map(c => c.label).join(',');
  const lines  = rows.map(r => COLS.map(c => `"${r[c.id] ?? ''}"`).join(','));
  const blob   = new Blob([[header, ...lines].join('\n')], { type: 'text/csv' });
  const url    = URL.createObjectURL(blob);
  const a      = document.createElement('a');
  a.href       = url;
  a.download   = `room-cancellations-${dates.start}-${dates.end}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function RoomCancellationsReport() {
  const [preset,      setPreset]      = useState('6m');
  const [customStart, setCustomStart] = useState('');
  const [customEnd,   setCustomEnd]   = useState('');
  const [data,        setData]        = useState(null);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState('');
  const [activeTab,   setActiveTab]   = useState(0);

  const dates = useMemo(() => {
    if (preset !== 'custom') return presetDates(preset);
    return customStart && customEnd ? { start: customStart, end: customEnd } : null;
  }, [preset, customStart, customEnd]);

  const runReport = useCallback(async () => {
    if (!dates) return;
    setLoading(true); setError(''); setData(null);
    try {
      const { data: resp } = await api.get('/reports/room-cancellations', {
        params: { start_date: dates.start, end_date: dates.end },
      });
      setData(resp);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [dates]);

  const breakdownRows = useMemo(() => {
    if (!data) return [];
    return [data.by_specialty, data.by_clinic, data.by_session][activeTab] || [];
  }, [data, activeTab]);

  const { summary } = data || {};

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 2.5 }}>
        <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', color: C.blue }}>
          Room Cancellations
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
          Compliance with the ≥4 weeks' notice requirement, average notice given, and ad hoc reallocation of freed space.
        </Typography>
      </Box>

      {/* Controls */}
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', mb: 2.5 }}>
        <ToggleButtonGroup value={preset} exclusive size="small"
          onChange={(_, v) => v && setPreset(v)}>
          {[['3m','Last 3 months'],['6m','Last 6 months'],['12m','Last 12 months'],['custom','Custom']].map(([v, lbl]) => (
            <ToggleButton key={v} value={v} sx={{ fontSize: '0.72rem', px: 1.5, py: 0.6, textTransform: 'none' }}>
              {lbl}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        {preset === 'custom' && (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)}
              style={{ padding: '5px 8px', border: '1px solid #ccc', borderRadius: 4, fontSize: '0.78rem' }} />
            <Typography variant="caption" color="text.secondary">→</Typography>
            <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)}
              style={{ padding: '5px 8px', border: '1px solid #ccc', borderRadius: 4, fontSize: '0.78rem' }} />
          </Box>
        )}

        <Button variant="contained" size="small" onClick={runReport}
          disabled={!dates || loading}
          startIcon={loading ? <CircularProgress size={13} color="inherit" /> : null}
          sx={{ bgcolor: C.blue, '&:hover': { bgcolor: '#004494' }, px: 2.5, py: 0.7,
            fontSize: '0.78rem', textTransform: 'none', fontWeight: 700 }}>
          {loading ? 'Loading…' : 'Run Report'}
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2, fontSize: '0.8rem' }}>{error}</Alert>}

      {/* ── Results ── */}
      {summary && (
        <>
          {/* Summary cards */}
          <Box sx={{ display: 'flex', gap: 1.5, mb: 2.5, flexWrap: 'wrap' }}>
            <StatCard
              label="Total Cancellations"
              primary={summary.total.toLocaleString()}
              secondary={`${dates.start} — ${dates.end}`}
              accentColor={C.blue}
            />
            <StatCard
              label="Compliant (≥4 Weeks)"
              primary={summary.compliant.toLocaleString()}
              secondary={`${summary.pct_compliant}% of cancellations`}
              accentColor={summary.pct_compliant >= 90 ? C.green : summary.pct_compliant >= 70 ? C.yellow : C.red}
            />
            <StatCard
              label="Non-Compliant (<4 Weeks)"
              primary={summary.non_compliant.toLocaleString()}
              secondary={`${summary.pct_non_compliant}% of cancellations`}
              accentColor={summary.non_compliant > 0 ? C.red : C.green}
            />
            <StatCard
              label="Avg Notice Given"
              primary={`${summary.avg_notice_days}d`}
              secondary={summary.avg_notice_days >= 28 ? 'Above 4-week threshold' : 'Below 4-week threshold'}
              accentColor={summary.avg_notice_days >= 28 ? C.green : C.red}
            />
            <StatCard
              label="Reallocated for Ad Hoc"
              primary={summary.reallocated_adhoc.toLocaleString()}
              secondary={`${summary.pct_reallocated}% of cancelled slots reused`}
              accentColor={C.blue}
            />
          </Box>

          {/* Compliance notice band */}
          {summary.pct_compliant < 90 && (
            <Alert severity={summary.pct_compliant < 70 ? 'error' : 'warning'}
              sx={{ mb: 2, fontSize: '0.78rem' }}>
              <strong>{summary.pct_non_compliant}%</strong> of cancellations ({summary.non_compliant.toLocaleString()} rooms) were
              cancelled with less than 4 weeks' notice in the selected period. The requirement is ≥4 weeks for all cancellations.
            </Alert>
          )}

          {/* Chart */}
          <Paper elevation={0} sx={{ border: `1px solid ${C.pale}`, borderRadius: 2, p: 2, mb: 2 }}>
            <Box sx={{ mb: 1.5 }}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: C.dark }}>
                Cancellations by Month — Compliance Breakdown
              </Typography>
              <Typography variant="caption" sx={{ color: C.mid }}>
                Stacked by compliance status  ·  % label = monthly compliance rate
              </Typography>
            </Box>
            <CancellationChart timeSeries={data.time_series} />
          </Paper>

          {/* Notice period distribution */}
          <Paper elevation={0} sx={{ border: `1px solid ${C.pale}`, borderRadius: 2, p: 2, mb: 2 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: C.dark, mb: 1 }}>
              Notice Period Distribution
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              {[
                { label: '< 4 weeks',        count: summary.non_compliant, color: C.red   },
                { label: '4–8 weeks',         count: data.time_series.reduce((s,r) => s + r.notice_4_8w, 0),  color: C.yellow },
                { label: '> 8 weeks',         count: data.time_series.reduce((s,r) => s + r.notice_over_8w, 0), color: C.green },
              ].map(({ label, count, color }) => (
                <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1,
                  border: `1px solid ${color}40`, borderRadius: 1.5, bgcolor: `${color}08` }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color }} />
                  <Box>
                    <Typography sx={{ fontSize: '0.7rem', color: C.mid }}>{label}</Typography>
                    <Typography sx={{ fontSize: '1rem', fontWeight: 700, color, lineHeight: 1.2 }}>
                      {count.toLocaleString()}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Paper>

          {/* Breakdown table */}
          <Paper elevation={0} sx={{ border: `1px solid ${C.pale}`, borderRadius: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              px: 2, pt: 1, pb: 0 }}>
              <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{
                minHeight: 38,
                '& .MuiTab-root': { fontSize: '0.75rem', minHeight: 38, py: 0.5, px: 2,
                  textTransform: 'none', fontWeight: 600 },
                '& .Mui-selected':      { color: `${C.blue} !important` },
                '& .MuiTabs-indicator': { backgroundColor: C.blue },
              }}>
                <Tab label={`By Specialty (${data.by_specialty.length})`} />
                <Tab label={`By Location (${data.by_clinic.length})`} />
                <Tab label="By Session" />
              </Tabs>
              <Button size="small" variant="outlined" startIcon={<Download sx={{ fontSize: '14px !important' }} />}
                onClick={() => exportCSV(breakdownRows, dates)}
                sx={{ borderColor: C.blue, color: C.blue, fontSize: '0.7rem',
                  py: 0.4, px: 1.5, textTransform: 'none' }}>
                CSV
              </Button>
            </Box>
            <Divider />
            <BreakdownTable rows={breakdownRows} />
          </Paper>

          {/* Data note */}
          <Alert severity="info" icon={<InfoOutlined sx={{ fontSize: 18 }} />}
            sx={{ fontSize: '0.75rem', '& .MuiAlert-message': { pt: '2px' } }}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.72rem', mb: 0.25 }}>Note on notice period data</Typography>
            <Typography sx={{ fontSize: '0.72rem' }}>
              Notice periods for cancellations made before June 2026 are approximated from the booking's
              last-updated timestamp, which may reflect edits rather than the precise cancellation date.
              Cancellations made from June 2026 onwards use an accurate <em>cancelled_at</em> timestamp.
              Ad hoc reallocation is detected by matching a confirmed ad hoc booking to the same room,
              date, and session as the cancelled booking.
            </Typography>
          </Alert>
        </>
      )}

      {/* Empty state */}
      {!data && !loading && (
        <Box sx={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', height: 180, color: C.mid, gap: 1,
          border: `1.5px dashed ${C.pale}`, borderRadius: 2, mt: 1,
        }}>
          <Typography variant="body2">Select a date range and click Run Report</Typography>
          {dates && (
            <Typography variant="caption" sx={{ color: C.pale }}>{dates.start} → {dates.end}</Typography>
          )}
        </Box>
      )}
    </Box>
  );
}
